import { nextTick } from "vue";
import { createRouter, createWebHistory } from "vue-router";
import HomeView from "../views/HomeView.vue";
import { updateMeta } from "@/utils/meta";
import { staticRoutes } from "@/constants/routeMeta";
import { useRouteProgress } from "@/composables/useRouteProgress";

// title / description は src/constants/routeMeta.js で一元管理
// （scripts/generate-seo-pages.js も同じモジュールを参照する）
function metaFor(path) {
  const route = staticRoutes.find((r) => r.path === path);
  return { title: route.title, description: route.description };
}

const routes = [
  {
    path: "/",
    name: "home",
    component: HomeView,
    meta: metaFor("/"),
  },
  {
    path: "/message",
    name: "message",
    component: () =>
      import(/* webpackChunkName: "message" */ "../views/MessageView.vue"),
    meta: metaFor("/message"),
  },
  {
    path: "/company",
    name: "company",
    component: () =>
      import(/* webpackChunkName: "company" */ "../views/CompanyView.vue"),
    meta: metaFor("/company"),
  },
  {
    path: "/projects",
    name: "projects",
    component: () =>
      import(/* webpackChunkName: "projects" */ "../views/ProjectsView.vue"),
    meta: metaFor("/projects"),
  },
  {
    path: "/blog",
    name: "blog",
    component: () =>
      import(/* webpackChunkName: "blog" */ "../views/BlogView.vue"),
    meta: metaFor("/blog"),
  },
  {
    path: "/blog/:id",
    name: "blog-detail",
    component: () =>
      import(/* webpackChunkName: "blog" */ "../views/BlogDetailView.vue"),
    // 記事別の title / description は BlogDetailView が設定する（ここは記事が見つからない場合のフォールバック）
    meta: {
      title: "会社ブログ",
      description: "合同会社エクスシップスの公式ブログ記事。",
    },
  },
  {
    path: "/contact",
    name: "contact",
    component: () =>
      import(/* webpackChunkName: "contact" */ "../views/ContactView.vue"),
    meta: metaFor("/contact"),
  },
  {
    path: "/privacy_policy",
    name: "privacy_policy",
    component: () =>
      import(/* webpackChunkName: "legal" */ "../views/PrivacyPolicyView.vue"),
    meta: metaFor("/privacy_policy"),
  },
  {
    path: "/terms_of_use",
    name: "terms_of_use",
    component: () =>
      import(/* webpackChunkName: "legal" */ "../views/TermsOfUseView.vue"),
    meta: metaFor("/terms_of_use"),
  },
  {
    // キャッチオール（404）。routeMeta.js の staticRoutes には載せない
    // （sitemap・静的HTML生成の対象外にするため。scripts/generate-seo-pages.js 参照）
    path: "/:pathMatch(.*)*",
    name: "not-found",
    component: () =>
      import(/* webpackChunkName: "not-found" */ "../views/NotFoundView.vue"),
    meta: {
      title: "ページが見つかりません",
      description:
        "お探しのページは移動または削除された可能性があります。合同会社エクスシップスのサイトです。",
      noindex: true,
    },
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }
    return { top: 0 };
  },
});

// 遅延チャンクの読み込みが長引くときだけ、画面上端に細いプログレスバーを出す
// （150ms 未満で終わる遷移では表示しない。実装は composables/useRouteProgress.js）
const routeProgress = useRouteProgress();
router.beforeEach(() => {
  routeProgress.start();
});

// View Transitions API によるページ遷移アニメーション
// 非対応ブラウザ・reduced-motion 環境では通常遷移にフォールバック
let finishViewTransition;

router.beforeResolve((to, from) => {
  if (!document.startViewTransition) return;
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
  // 初回ロードと、ハッシュ・クエリのみの変更は対象外
  if (from.matched.length === 0 || to.path === from.path) return;

  // 前の遷移が未完了のまま残っていたら先に完了させる（連続クリック対策）
  finishViewTransition?.();

  const domUpdated = new Promise((resolve) => {
    finishViewTransition = resolve;
  });
  document.startViewTransition(() => domUpdated);
});

router.afterEach(async () => {
  routeProgress.finish();
  if (!finishViewTransition) return;
  // 新しいページの DOM 反映を待ってから新スナップショットを撮らせる
  await nextTick();
  finishViewTransition();
  finishViewTransition = undefined;
});

router.onError(() => {
  routeProgress.finish();
  finishViewTransition?.();
  finishViewTransition = undefined;
});

// ページ遷移時にタイトル・metaタグを動的更新
// blog-detail は BlogDetailView が記事データから記事別のメタを設定する
// （afterEach → mounted の順に実行されるため、記事別の値が最終的に反映される）
router.afterEach((to) => {
  updateMeta({
    title: to.meta.title,
    description: to.meta.description,
    path: to.path,
    noindex: to.meta.noindex === true,
  });
});

export default router;
